import posthog from "posthog-js";

/**
 * PostHog (product analytics) — pendant web du `posthog.tsx` mobile.
 * Même instance self-hosted que l'app mobile ; les events web portent la
 * super-propriété `platform: web` pour les distinguer.
 */

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://ingest-customer.fastsvc.net";

// Forcer l'envoi en local pour vérifier l'intégration sans build prod :
// NEXT_PUBLIC_ANALYTICS_DEBUG=true dans .env, puis relancer le serveur.
const FORCE_ENABLE = process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === "true";

/** Activé en production (ou en local si le flag debug est posé) et si une clé existe. */
export const POSTHOG_ENABLED =
  !!POSTHOG_KEY && (process.env.NODE_ENV === "production" || FORCE_ENABLE);

/**
 * Clés de propriétés volumineuses (format e-commerce GA4) sans intérêt dans
 * PostHog : la liste complète des produits est destinée à Google pour
 * l'attribution. On la retire AVANT ingestion pour ne pas gonfler le stockage
 * ni la cardinalité. Les scalaires utiles (value, currency, store_id…) restent.
 */
const HEAVY_PROP_KEYS = ["items", "products", "menu", "categories", "cart_items"];

function stripHeavyProps(
  properties: Record<string, unknown>
): Record<string, unknown> {
  let cleaned: Record<string, unknown> | undefined;
  for (const key of Object.keys(properties)) {
    const value = properties[key];
    const isHeavy =
      HEAVY_PROP_KEYS.includes(key) ||
      (Array.isArray(value) &&
        value.some((v) => v !== null && typeof v === "object"));
    if (isHeavy) {
      if (!cleaned) cleaned = { ...properties };
      delete cleaned[key];
    }
  }
  return cleaned ?? properties;
}

let _initialized = false;

/** Initialise PostHog côté navigateur. Idempotent et silencieux si désactivé. */
export function initPostHog(): void {
  if (_initialized || typeof window === "undefined" || !POSTHOG_ENABLED) return;

  posthog.init(POSTHOG_KEY as string, {
    api_host: POSTHOG_HOST,
    // Profils créés seulement après identify → coût/cardinalité maîtrisés.
    person_profiles: "identified_only",
    // SPA App Router : on capture les pageviews manuellement sur changement de route.
    capture_pageview: false,
    capture_pageleave: true,
    // Allège chaque payload : jamais les listes de produits/menu dans PostHog.
    sanitize_properties: (properties) => stripHeavyProps(properties),
    loaded: (ph) => {
      ph.register({ platform: "web" });
    },
  });

  _initialized = true;
}

export { posthog };

/** Identifier l'utilisateur (login/signup). Appeler posthogReset() au logout. */
export function identifyPostHogUser(user: {
  id?: string;
  phone?: string;
  email?: string;
  name?: string;
}): void {
  if (!POSTHOG_ENABLED) return;
  // PostHog n'accepte pas `undefined` dans les properties → on filtre.
  const properties: Record<string, string> = {};
  if (user.email) properties.email = user.email;
  if (user.name) properties.name = user.name;
  if (user.phone) properties.phone = user.phone;
  posthog.identify(user.phone ?? user.id, properties);
}

/** Reset PostHog (logout) — régénère un anonymousId. */
export function posthogReset(): void {
  if (!POSTHOG_ENABLED) return;
  posthog.reset();
}
